import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, distinct, mergeMap, last, map } from 'rxjs/operators';
import * as ExoActions from './actions';
import { SafeLinksPolicy } from './model';
import { retry } from 'projects/angular-clarity/src/app/pipes/retry.pipe';
import { TenantAjaxService } from 'projects/angular-clarity/src/app/services/ajax/tenant-ajax.service';

const EndPoint = '/api/microsoft/powershell/exo/safe-links-policy';

@Injectable()
export class SafeLinksPolicyEffects {
    fetchSafeLinksPolicy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExoActions.loadSafeLinksPolicy),
            distinct((action) => action._tenant),
            mergeMap(({ _tenant }) =>
                this.get(_tenant).pipe(
                    retry(3000, 3, 'safe links policy timeout'),
                    last(),
                    map((data: SafeLinksPolicy[]) => ExoActions.loadSafeLinksPolicySuccess({ _tenant, data })),
                    catchError((error) => of(ExoActions.loadSafeLinksPolicyFailure({ _tenant, error }))),
                ),
            ),
        ),
    );

    createSafeLinksPolicy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExoActions.createExoSafeLinksPolicy),
            mergeMap((action) =>
                this.post(action._tenant, action.data).pipe(
                    map((res) => ({ ...res[0] })),
                    map((data: SafeLinksPolicy) =>
                        ExoActions.createExoSafeLinksPolicySuccess({ _tenant: action._tenant, data }),
                    ),
                    catchError((error) =>
                        of(
                            ExoActions.createExoSafeLinksPolicyFailure({
                                _tenant: action._tenant,
                                error: { message: error.message, trace: error.trace },
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    updateSafeLinksPolicy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExoActions.updateExoSafeLinksPolicy),
            mergeMap((action) =>
                this.put(action._tenant, action.data).pipe(
                    map((res) => ({ ...res[0] })),
                    map((data: SafeLinksPolicy) =>
                        ExoActions.updateExoSafeLinksPolicySuccess({ _tenant: action._tenant, data }),
                    ),
                    catchError((error) =>
                        of(
                            ExoActions.updateExoSafeLinksPolicyFailure({
                                _tenant: action._tenant,
                                error: { message: error.message, trace: error.trace },
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    private get(tenant: string) {
        return this.ajax.get(tenant, EndPoint);
    }

    private post(tenant: string, body: Partial<SafeLinksPolicy>): Observable<SafeLinksPolicy[]> {
        return this.ajax.post(tenant, EndPoint, body);
    }

    private put(tenant: string, body: Partial<SafeLinksPolicy>): Observable<SafeLinksPolicy[]> {
        return this.ajax.put(tenant, `${EndPoint}`, body);
    }

    constructor(
        private actions$: Actions,
        private ajax: TenantAjaxService,
    ) {}
}
